import type { ReactElement, ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SharedTooltip } from '@/components/shared/tooltip';

interface InfoTooltipProps {
  /** Tooltip body. */
  content: ReactNode;
  /**
   * What the icon explains, e.g. "Risk score" — read by screen readers as
   * "About Risk score", since the icon itself carries no meaning.
   */
  label: string;
  className?: string;
}

/**
 * KIR-4836 — the small `i` affordance the tooltip mockup puts next to every
 * number in the tool-call panel.
 *
 * A real `<button>` rather than a bare icon so the explanation is reachable by
 * keyboard, not hover only. `SharedTooltip` supplies its own provider, so this
 * needs no wrapping context.
 */
export function InfoTooltip({
  content,
  label,
  className,
}: InfoTooltipProps): ReactElement {
  return (
    <SharedTooltip content={content}>
      <button
        type="button"
        aria-label={`About ${label}`}
        data-testid="info-tooltip-trigger"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
    </SharedTooltip>
  );
}

InfoTooltip.displayName = 'InfoTooltip';
